import { autoUpdate, computePosition, flip, offset, shift, type Placement } from '@floating-ui/dom';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

interface FloatingMenuOptions {
  placement?: Placement;
  // Visual distance between the trigger and the popover surface, in px. Rendered
  // as padding on the popover's trigger-facing side (see FloatingPopover), NOT as
  // a floating-ui offset — so the popover's hover region stays flush against the
  // trigger and the pointer never crosses a dead gap on its way over.
  gap?: number;
  crossAxis?: number;
  // Backstop for pointer travel time. The contiguous gap above does the real work;
  // this only covers a path that clips a trigger corner.
  hideDelay?: number;
  // When false the menu can never open (e.g. nav popovers while expanded).
  enabled?: boolean;
}

const DEFAULT_GAP = 8;
const DEFAULT_HIDE_DELAY = 150;

// Which edge of the popover faces the trigger, per resolved placement — flip()
// can land us on the opposite side of the requested one, and the bridge padding
// has to follow.
const BRIDGE_PADDING_PROPERTY = {
  top: 'paddingBottom',
  bottom: 'paddingTop',
  left: 'paddingRight',
  right: 'paddingLeft',
} as const;

type PlacementSide = keyof typeof BRIDGE_PADDING_PROPERTY;

// At most one sidebar popover is open at a time, tracked across every hook
// instance. Each row owns an independent close timer, so without this a pointer
// swept down the collapsed rail leaves a trail — one lingering popover per row it
// crossed, all still inside their hideDelay. Opening a menu closes whoever holds
// the slot, which makes moving between rows an instant swap.
let releaseOpenMenu: (() => void) | null = null;

// A thin React wrapper around @floating-ui/dom that positions a hover-triggered
// popover and portals it to the body. Replaces the floating_dropdown /
// user_profile_hover Stimulus controllers. Cleans up its autoUpdate + timers on
// unmount.
export function useFloatingMenu({
  placement = 'right-start',
  gap = DEFAULT_GAP,
  crossAxis = 0,
  hideDelay = DEFAULT_HIDE_DELAY,
  enabled = true,
}: FloatingMenuOptions = {}) {
  const referenceRef = useRef<HTMLElement | null>(null);
  const floatingRef = useRef<HTMLDivElement | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [resolvedPlacement, setResolvedPlacement] = useState<Placement>(placement);

  // Stable callback ref so call sites can write `ref={menu.setReference}`.
  const setReference = useCallback((el: HTMLElement | null) => {
    referenceRef.current = el;
  }, []);

  // Stable identity for the single-open slot, so `show` can tell "the slot is
  // mine" from "the slot belongs to another row".
  const close = useRef(() => {});
  close.current = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (releaseOpenMenu === close.current) releaseOpenMenu = null;
    setOpen(false);
  };

  const show = useCallback(() => {
    if (!enabled) return;
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (releaseOpenMenu && releaseOpenMenu !== close.current) releaseOpenMenu();
    releaseOpenMenu = close.current;
    setOpen(true);
  }, [enabled]);

  const scheduleHide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => close.current(), hideDelay);
  }, [hideDelay]);

  // Force-close when the menu becomes disabled (e.g. sidebar expands).
  useEffect(() => {
    if (!enabled) close.current();
  }, [enabled]);

  // The sidebar is turbo-permanent, so its popovers (portalled to document.body)
  // would otherwise survive a navigation and linger over the next page. Close
  // before Turbo swaps the page.
  useEffect(() => {
    const onTurbo = () => close.current();
    document.addEventListener('turbo:before-render', onTurbo);
    document.addEventListener('turbo:before-cache', onTurbo);
    return () => {
      document.removeEventListener('turbo:before-render', onTurbo);
      document.removeEventListener('turbo:before-cache', onTurbo);
    };
  }, []);

  useEffect(() => {
    const reference = referenceRef.current;
    const floating = floatingRef.current;
    if (!open || !reference || !floating) return;

    const update = () => {
      // mainAxis stays 0 — the trigger-facing gap is the popover's own padding, so
      // floating-ui positions the popover flush and the padding renders the space.
      void computePosition(reference, floating, {
        placement,
        strategy: 'fixed',
        middleware: [offset({ crossAxis }), flip(), shift({ padding: 8 })],
      }).then(({ x, y, placement: finalPlacement }) => {
        setCoords({ x, y });
        setResolvedPlacement(finalPlacement);
      });
    };

    return autoUpdate(reference, floating, update);
  }, [open, placement, crossAxis]);

  useEffect(() => () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    // Don't leave the single-open slot pointing at an unmounted menu.
    if (releaseOpenMenu === close.current) releaseOpenMenu = null;
  }, []);

  // Padding that makes the popover's hover region contiguous with the trigger.
  const bridgeStyle = useMemo<CSSProperties>(() => {
    const side = resolvedPlacement.split('-')[0] as PlacementSide;
    return { [BRIDGE_PADDING_PROPERTY[side]]: gap };
  }, [resolvedPlacement, gap]);

  return { setReference, floatingRef, open, coords, bridgeStyle, show, scheduleHide };
}

export type FloatingMenu = ReturnType<typeof useFloatingMenu>;
